import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { Shield, Loader2, AlertTriangle, KeyRound } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface TwoFactorVerifyProps {
  factorId: string;
  onComplete: () => void;
  onCancel: () => void;
}

export const TwoFactorVerify = ({ factorId, onComplete, onCancel }: TwoFactorVerifyProps) => {
  const [verificationCode, setVerificationCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleVerify = async () => {
    if (verificationCode.length !== 6) return;

    setIsLoading(true);
    setError(null);

    try {
      // Criar challenge
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId,
      });

      if (challengeError) {
        console.error('Erro ao criar challenge:', challengeError);
        setError('Erro ao verificar código. Tente novamente.');
        return;
      }

      // Verificar código
      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challengeData.id,
        code: verificationCode,
      });

      if (verifyError) {
        console.error('Erro ao verificar:', verifyError);
        if (verifyError.message.includes('Invalid')) {
          setError('Código inválido. Verifique e tente novamente.');
        } else {
          setError('Erro ao verificar código. Tente novamente.');
        }
        setVerificationCode('');
        return;
      }

      // Sucesso!
      onComplete();
    } catch (err) {
      console.error('Erro inesperado:', err);
      setError('Erro inesperado. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  // Auto-submit when 6 digits are entered
  const handleCodeChange = (value: string) => {
    setVerificationCode(value);
    if (value.length === 6) {
      // Small delay to show the last digit before submitting
      setTimeout(() => {
        handleVerify();
      }, 100);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4">
      {/* Background pattern */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMyMDIwMjAiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRjMC0yLjIwOS0xLjc5MS00LTQtNHMtNCAxLjc5MS00IDQgMS43OTEgNCA0IDQgNC0xLjc5MSA0LTR6bTAtMTdjMC0yLjIwOS0xLjc5MS00LTQtNHMtNCAxLjc5MS00IDQgMS43OTEgNCA0IDQgNC0xLjc5MSA0LTR6bTE3IDE3YzAtMi4yMDktMS43OTEtNC00LTRzLTQgMS43OTEtNCA0IDEuNzkxIDQgNCA0IDQtMS43OTEgNC00em0wLTE3YzAtMi4yMDktMS43OTEtNC00LTRzLTQgMS43OTEtNCA0IDEuNzkxIDQgNCA0IDQtMS43OTEgNC00em0tMzQgMTdjMC0yLjIwOS0xLjc5MS00LTQtNHMtNCAxLjc5MS00IDQgMS43OTEgNCA0IDQgNC0xLjc5MSA0LTR6bTAtMTdjMC0yLjIwOS0xLjc5MS00LTQtNHMtNCAxLjc5MS00IDQgMS43OTEgNCA0IDQgNC0xLjc5MSA0LTR6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-30" />

      <div className="w-full max-w-md relative z-10">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <div className="p-4 rounded-full bg-gradient-to-br from-amber-500/20 to-orange-500/20 border border-amber-500/30">
              <Shield className="h-12 w-12 text-amber-500" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Verificação de Dois Fatores</h1>
          <p className="text-slate-400 text-sm">
            Digite o código do seu aplicativo autenticador
          </p>
        </div>

        <Card className="shadow-2xl border-slate-800 bg-slate-900/80 backdrop-blur-sm">
          <CardHeader className="text-center pb-4">
            <CardTitle className="text-lg text-white flex items-center justify-center gap-2">
              <KeyRound className="h-5 w-5 text-amber-500" />
              Código de Verificação
            </CardTitle>
            <CardDescription className="text-slate-400">
              Abra seu aplicativo autenticador e digite o código de 6 dígitos
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {error && (
              <Alert variant="destructive" className="bg-red-950/50 border-red-900">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Verification Input */}
            <div className="space-y-4">
              <div className="flex justify-center">
                <InputOTP
                  maxLength={6}
                  value={verificationCode}
                  onChange={handleCodeChange}
                  disabled={isLoading}
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} className="bg-slate-800 border-slate-700 text-white h-14 w-12 text-xl" />
                    <InputOTPSlot index={1} className="bg-slate-800 border-slate-700 text-white h-14 w-12 text-xl" />
                    <InputOTPSlot index={2} className="bg-slate-800 border-slate-700 text-white h-14 w-12 text-xl" />
                    <InputOTPSlot index={3} className="bg-slate-800 border-slate-700 text-white h-14 w-12 text-xl" />
                    <InputOTPSlot index={4} className="bg-slate-800 border-slate-700 text-white h-14 w-12 text-xl" />
                    <InputOTPSlot index={5} className="bg-slate-800 border-slate-700 text-white h-14 w-12 text-xl" />
                  </InputOTPGroup>
                </InputOTP>
              </div>
              
              {isLoading && (
                <div className="flex items-center justify-center gap-2 text-amber-500">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm">Verificando...</span>
                </div>
              )}
            </div>

            <Button
              onClick={handleVerify}
              disabled={isLoading || verificationCode.length !== 6}
              className="w-full bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 text-white"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Verificando...
                </>
              ) : (
                <>
                  <Shield className="mr-2 h-4 w-4" />
                  Verificar e Acessar
                </>
              )}
            </Button>

            <div className="text-center">
              <Button
                variant="ghost"
                onClick={onCancel}
                className="text-slate-400 hover:text-white hover:bg-slate-800"
              >
                Cancelar e Sair
              </Button>
            </div>

            <p className="text-xs text-center text-slate-500">
              Não tem acesso ao autenticador?{' '}
              <a href="mailto:suporte@gonx.com.br" className="text-amber-500 hover:underline">
                Contate o suporte
              </a>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
